// Fix GS (2026-10-17): "al momento de cerrar una vinculación con diferencia, se detalla porque la diferencia.
// Al cerrar la vinculación debe poder visualizarse el porque hubo la diferencia y se cerró de todas formas"
// (CEN). La ventana de cierre muestra la diferencia (montos y, en la OC, los ítems que no coinciden) y pide
// el motivo; después, el cierre se ve a la vista (no solo al pasar el mouse) en las tarjetas y en las
// ventanas de facturas de Materiales y de Servicios, y en la Orden de Pago.
import { useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { formatPYG, formatDate } from "../../utils/format";
import { Modal } from "./SharedComponents";

const MOTIVOS_RAPIDOS = [
  "El proveedor no tenía todo lo pedido",
  "El proveedor entregó y facturó menos",
  "Descuento o bonificación del proveedor",
  "Nota de crédito del proveedor",
  "Diferencia de redondeo",
];

const cant = (v) => Number(v || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });

// Ítems de la OC que no coinciden con lo facturado (la foto del cierre o, en cierres anteriores, la cobertura actual).
export const itemsConDiferencia = (doc, cov) => (doc?.vinculacion_cerrada_detalle?.items
  || (cov?.has_item_assignments ? (cov.items || []).filter(c => c.status !== "ok") : []));

export function textoCierre(doc, cov) {
  const det = doc?.vinculacion_cerrada_detalle || {};
  const dif = Number(doc?.vinculacion_cerrada_diferencia ?? det.diferencia ?? 0);
  if (dif > 1) return `Cerrada con diferencia de ${formatPYG(dif)}`;
  if (Number(det.exceso || 0) > 1) return `Cerrada con ${formatPYG(det.exceso)} facturado de más`;
  if (itemsConDiferencia(doc, cov).length) return "Cerrada con diferencia en cantidades";
  return "Vinculación cerrada";
}

export function BadgeCierre({ doc, cov, testId }) {
  return (
    <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-300 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
      data-testid={testId}>
      <Lock size={10} /> {textoCierre(doc, cov)}
    </span>
  );
}

// El cierre a la vista: motivo, quién y cuándo, montos y los ítems con diferencia.
export function VinculacionCerradaInfo({ doc, cov, testId = "vinc-cerrada-info" }) {
  if (!doc?.vinculacion_facturas_cerrada) return null;
  const det = doc.vinculacion_cerrada_detalle;
  const items = itemsConDiferencia(doc, cov);
  return (
    <div className="mt-1.5 text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700 space-y-0.5" data-testid={testId}>
      <p><span className="font-semibold">Motivo del cierre:</span> {doc.vinculacion_cerrada_motivo ? `«${doc.vinculacion_cerrada_motivo}»` : "sin motivo cargado"}</p>
      <p className="text-slate-500">
        {textoCierre(doc, cov)}
        {det ? ` · facturado ${formatPYG(det.facturado)} de ${formatPYG(det.total)}` : ""}
        {doc.vinculacion_cerrada_por ? ` · ${doc.vinculacion_cerrada_por}` : ""}
        {doc.vinculacion_cerrada_at ? `, ${formatDate(doc.vinculacion_cerrada_at)}` : ""}
      </p>
      {items.length > 0 && (
        <ul className="list-disc pl-4 text-slate-600">
          {items.map((it, i) => (
            <li key={i}>{it.item_name || "Ítem"}: OC {cant(it.oc_quantity)} {it.unit || ""} · facturado {cant(it.invoiced_quantity)} {it.unit || ""}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Ventana de cierre (reemplaza el cuadro del navegador): muestra la diferencia y pide el motivo.
export function CerrarVinculacionModal({ doc, etiqueta, total, facturado, items = [], onConfirm, onClose }) {
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const diferencia = Math.max(Number(total || 0) - Number(facturado || 0), 0);
  const exceso = Math.max(Number(facturado || 0) - Number(total || 0), 0);
  const valido = motivo.trim().length >= 5;
  const confirmar = async () => {
    if (!valido) return;
    setEnviando(true);
    try { await onConfirm(motivo.trim()); } finally { setEnviando(false); }
  };
  return (
    <Modal open onClose={() => !enviando && onClose()} title={`Cerrar vinculación · ${doc.number}`} wide data-testid="cerrar-vinc-modal">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{etiqueta} queda facturada con lo facturado hasta ahora. El motivo queda a la vista en Facturas y en la Orden de Pago.</p>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2"><p className="text-gray-500">Total</p><p className="font-bold text-gray-900">{formatPYG(total)}</p></div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2"><p className="text-gray-500">Facturado</p><p className="font-bold text-gray-900">{formatPYG(facturado)}</p></div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="cerrar-vinc-diferencia">
            <p className="text-amber-700">{exceso > 1 ? "Facturado de más" : "Diferencia"}</p>
            <p className="font-bold text-amber-800">{formatPYG(exceso > 1 ? exceso : diferencia)}</p>
          </div>
        </div>
        {items.length > 0 && (
          <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="cerrar-vinc-items">
            <table className="w-full text-xs">
              <thead className="bg-slate-100"><tr>
                <th className="text-left px-3 py-2 font-bold text-gray-500">Ítem con diferencia</th>
                <th className="text-right px-3 py-2 font-bold text-gray-500">OC</th>
                <th className="text-right px-3 py-2 font-bold text-gray-500">Facturado</th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-1.5">{it.item_name || "Ítem"}</td>
                    <td className="px-3 py-1.5 text-right">{cant(it.oc_quantity)} {it.unit || ""}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{cant(it.invoiced_quantity)} {it.unit || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700">¿Por qué se cierra con esta diferencia? *</label>
          <div className="flex flex-wrap gap-1.5">
            {MOTIVOS_RAPIDOS.map(m => (
              <button key={m} type="button" onClick={() => setMotivo(p => (p.trim() ? `${p.trim()}. ${m}` : m))}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-300 text-gray-600 hover:border-[#2E6DA4]/60 hover:text-[#2E6DA4]">
                {m}
              </button>
            ))}
          </div>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} autoFocus
            placeholder="Detallá el motivo de la diferencia..."
            className="w-full border border-gray-300 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
            data-testid="cerrar-vinc-motivo" />
          {!valido && motivo.trim().length > 0 && <p className="text-[11px] text-amber-700">Escribí un poco más: el motivo es lo que explica la diferencia.</p>}
        </div>
        <div className="flex gap-3 pt-1">
          <button onClick={onClose} disabled={enviando} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-50">Cancelar</button>
          <button onClick={confirmar} disabled={!valido || enviando}
            className="flex-1 bg-slate-800 hover:bg-slate-900 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="cerrar-vinc-confirmar">
            {enviando ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Cerrar vinculación
          </button>
        </div>
      </div>
    </Modal>
  );
}
